import 'react-native-get-random-values'

import React, { useState } from 'react'
import { Alert, FlatList, ImageBackground, StyleSheet, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Button, TextInput, List, Divider, Checkbox } from 'react-native-paper'
import { useRouter } from 'expo-router'
import { useQuery, useAction } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { debounce } from 'lodash'

import image from '../../../assets/icons/Home_Purple.png'
import styles from '../../styles/globalStyle'
import { DS } from '../../design-system'

const startStyles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column'
  },
  image: {
    flex: 1,
    resizeMode: 'cover',
    justifyContent: 'center'
  }
})

export const AdminStressTestScreen = () => {
  const router = useRouter()
  const [gameCount, setGameCount] = useState('50')
  const [botCount, setBotCount] = useState('9')
  const [startGames, setStartGames] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [isEnding, setIsEnding] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const stressTestGames = useQuery(api.metadata.queries.listStressTestGames, {})
  const createGames = useAction(api.games.actions.createBulkPracticeGames)
  const startBulkGames = useAction(api.games.actions.startBulkGames)
  const endGames = useAction(api.games.actions.endBulkGames)
  const deleteGames = useAction(api.games.actions.deleteBulkGames)

  const handleCreateGames = async () => {
    const count = parseInt(gameCount) || 50
    const bots = Math.min(9, Math.max(1, parseInt(botCount) || 9))

    if (count < 1 || count > 1000) {
      Alert.alert('Invalid Input', 'Number of games must be between 1 and 1000')
      return
    }

    setIsCreating(true)
    try {
      const result = await createGames({
        count,
        botCount: bots,
        bigBlind: 100,
        startingChips: 10000,
        startGames
      })
      Alert.alert('Success', `Created ${result.count} stress test games!${startGames ? ' All games started.' : ''}`)
    } catch (error) {
      Alert.alert('Error', error instanceof Error ? error.message : 'Failed to create games')
    } finally {
      setIsCreating(false)
    }
  }

  const handleStartAllGames = async () => {
    const pregameGames = stressTestGames?.filter((g) => g.state === 'pregame') || []
    if (!pregameGames.length) {
      Alert.alert('No Games', 'No pregame games to start')
      return
    }

    setIsStarting(true)
    try {
      await startBulkGames({
        gameIds: pregameGames.map((g) => g.gameId)
      })
      Alert.alert('Success', `Started ${pregameGames.length} games`)
    } catch (error) {
      Alert.alert('Error', error instanceof Error ? error.message : 'Failed to start games')
    } finally {
      setIsStarting(false)
    }
  }

  const handleEndAllGames = () => {
    if (!stressTestGames?.length) return

    Alert.alert('End All Games?', `This will end ${stressTestGames.length} games. Continue?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'End All',
        style: 'destructive',
        onPress: async () => {
          setIsEnding(true)
          try {
            await endGames({
              gameIds: stressTestGames.map((g) => g.gameId)
            })
            Alert.alert('Success', `Ended ${stressTestGames.length} games`)
          } catch (error) {
            Alert.alert('Error', error instanceof Error ? error.message : 'Failed to end games')
          } finally {
            setIsEnding(false)
          }
        }
      }
    ])
  }

  const handleDeleteAllGames = () => {
    if (!stressTestGames?.length) return

    Alert.alert(
      'Delete All Games?',
      `This will PERMANENTLY DELETE ${stressTestGames.length} games and all related data. This action cannot be undone. Continue?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete Permanently',
          style: 'destructive',
          onPress: async () => {
            setIsDeleting(true)
            try {
              await deleteGames({
                gameIds: stressTestGames.map((g) => g.gameId)
              })
              Alert.alert('Success', `Permanently deleted ${stressTestGames.length} games`)
            } catch (error) {
              Alert.alert('Error', error instanceof Error ? error.message : 'Failed to delete games')
            } finally {
              setIsDeleting(false)
            }
          }
        }
      ]
    )
  }

  return (
    <ImageBackground source={image} style={startStyles.image}>
      <View style={{ backgroundColor: 'rgba(255,255,255,.9)', flex: 1 }}>
        <SafeAreaView style={styles.container}>
          {/* Back Button */}
          <View style={{ paddingHorizontal: 16, paddingTop: 8, alignItems: 'flex-start' }}>
            <Button mode="text" icon="arrow-left" onPress={() => router.back()}>
              Back
            </Button>
          </View>

          {/* Configuration Section */}
          <View style={{ padding: 16 }}>
            <DS.Text variant="Heading 3/Medium">Configuration</DS.Text>
            <TextInput
              label="Number of Games (1-1000)"
              keyboardType="numeric"
              value={gameCount}
              onChangeText={setGameCount}
              mode="outlined"
              style={{ marginTop: 12 }}
            />
            <TextInput
              label="Bots per Game (1-9)"
              keyboardType="numeric"
              value={botCount}
              onChangeText={setBotCount}
              mode="outlined"
              style={{ marginTop: 12 }}
            />
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 12 }}>
              <Checkbox status={startGames ? 'checked' : 'unchecked'} onPress={() => setStartGames(!startGames)} />
              <DS.Text variant="Body Large/Regular" onPress={() => setStartGames(!startGames)}>
                Start games immediately
              </DS.Text>
            </View>
            <Button
              mode="contained"
              onPress={debounce(handleCreateGames, 250)}
              disabled={isCreating}
              loading={isCreating}
              icon="plus-circle"
              style={{ marginTop: 12 }}
            >
              Create Games
            </Button>
          </View>

          <Divider />

          {/* Games List Section */}
          <View style={{ flex: 1, padding: 16 }}>
            <DS.Text variant="Heading 3/Medium">Active Tests ({stressTestGames?.length || 0} games)</DS.Text>
            <FlatList
              data={stressTestGames}
              keyExtractor={(item) => item.gameId}
              renderItem={({ item }) => (
                <List.Item
                  title={item.name}
                  description={`${item.playerCount}/${item.maxPlayers} • ${item.state}`}
                  right={() => (
                    <Button
                      mode="outlined"
                      compact
                      onPress={debounce(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        () => router.navigate({ pathname: '/game/[gameId]', params: { gameId: item.gameId } } as any),
                        250
                      )}
                    >
                      View
                    </Button>
                  )}
                />
              )}
              style={{ marginTop: 12 }}
            />
          </View>

          <Divider />

          {/* Actions Section */}
          <View style={{ padding: 16, gap: 12 }}>
            <Button
              mode="contained"
              buttonColor="#2e7d32"
              onPress={debounce(handleStartAllGames, 250)}
              disabled={!stressTestGames?.some((g) => g.state === 'pregame') || isStarting}
              loading={isStarting}
              icon="play-circle"
            >
              Start All Pregame Games
            </Button>
            <Button
              mode="contained"
              buttonColor="#d32f2f"
              onPress={debounce(handleEndAllGames, 250)}
              disabled={!stressTestGames?.length || isEnding}
              loading={isEnding}
              icon="stop-circle"
            >
              End All Games
            </Button>
            <Button
              mode="contained"
              buttonColor="#b71c1c"
              onPress={debounce(handleDeleteAllGames, 250)}
              disabled={!stressTestGames?.length || isDeleting}
              loading={isDeleting}
              icon="delete-forever"
            >
              Delete All Games
            </Button>
          </View>
        </SafeAreaView>
      </View>
    </ImageBackground>
  )
}
